import useCallWhenReady from "#/src/hooks/useCallWhenReady";
import useIsMobile from "#/src/hooks/useIsMobile";
import * as logClient from "@classdojo/log-client";
import { logConvertExperiment, logStartExperiment, setEntityType } from "@classdojo/log-client";
import { useOnFirstRender } from "@web-monorepo/hooks";
import {
  setFeatureSwitchesConfig,
  useEnsureFeatureSwitches,
  useFetchedFeatureSwitch,
} from "@web-monorepo/feature-switches";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { LOGGED_IN_SWITCH_MAP, LOGGED_OUT_SWITCH_MAP } from "#/src/constants/featureSwitches";
import { useSessionQuery } from "#/src/hooks/useSession";
import { DevToolsSettings } from "#/src/utils/devTools";

const getRole = (roles: string[]) => (roles.includes("teacher") ? "tutor" : "parent");

export const LOGGED_OUT_SWITCH_NAMES = Object.keys(LOGGED_OUT_SWITCH_MAP) as Array<keyof typeof LOGGED_OUT_SWITCH_MAP>;
export const LOGGED_IN_SWITCH_NAMES = Object.keys(LOGGED_IN_SWITCH_MAP) as Array<keyof typeof LOGGED_IN_SWITCH_MAP>;
const FEATURE_SWITCH_VALUES = { ...LOGGED_OUT_SWITCH_MAP, ...LOGGED_IN_SWITCH_MAP } as const;

export type FeatureName = (typeof LOGGED_OUT_SWITCH_NAMES)[number] | (typeof LOGGED_IN_SWITCH_NAMES)[number];
export type LoggedOutFeatureSwitches = {
  [key in (typeof LOGGED_OUT_SWITCH_NAMES)[number]]: (typeof LOGGED_OUT_SWITCH_MAP)[key]["values"][number];
};
export type LoggedInFeatureSwitches = {
  [key in (typeof LOGGED_IN_SWITCH_NAMES)[number]]: (typeof LOGGED_IN_SWITCH_MAP)[key]["values"][number];
};
type FeatureSwitches = LoggedOutFeatureSwitches & LoggedInFeatureSwitches;
type LoggedOutFeatureSwitchQueryParams = {
  featureSetId: "1";
  sessionId: string;
  sessionSwitches: string;
};
type LoggedInFeatureSwitchQueryParams = LoggedOutFeatureSwitchQueryParams &
  (
    | {
        parentId: string;
        parentCountryCode: "US";
        parentSwitches: string;
      }
    | {
        tutorId: string;
        tutorCountryCode: "US";
        tutorSwitches: string;
      }
  );
type FeatureSwitchQueryParams = LoggedOutFeatureSwitchQueryParams | LoggedInFeatureSwitchQueryParams;

interface FeatureSwitchOptions {
  isInAudience?: boolean;
}

export const useManualFeatureSwitchValue = <TSwitchName extends FeatureName>(
  switchName: TSwitchName,
  { isInAudience = true }: FeatureSwitchOptions = {},
): {
  data: FeatureSwitches[TSwitchName] | undefined;
  convertExperiment: (onConverted?: () => void) => void;
  startExperiment: (onStarted?: () => void) => void;
  isInExperiment: boolean;
} => {
  const featureSwitch = useFetchedFeatureSwitch(switchName) as FeatureSwitches[TSwitchName];
  const callWhenReady = useCallWhenReady(!!featureSwitch);

  const switchMetadata = useMemo(() => FEATURE_SWITCH_VALUES[switchName] || {}, [switchName]);
  if ("entityType" in switchMetadata) {
    setEntityType(switchMetadata.entityType);
  }

  const startExperiment = useCallback(
    (onStarted?: () => void) => {
      callWhenReady(() => {
        if ("entityType" in switchMetadata) {
          setEntityType(switchMetadata.entityType);
        }

        const logClientSwitches = logClient.getFeatureSwitches();
        if (logClientSwitches && logClientSwitches[switchName]) {
          logStartExperiment(switchName);
          onStarted?.();
        } else {
          logClient.logMessage(
            "WARN",
            `tried to start a feature switch before feature switch context set in log client for switch. retrying in 100ms`,
            { switchName },
          );
          setTimeout(() => {
            const retryLogClientSwitches = logClient.getFeatureSwitches();
            if (retryLogClientSwitches && retryLogClientSwitches[switchName]) {
              logStartExperiment(switchName);
              onStarted?.();
            } else {
              logClient.logMessage("ERROR", `feature switch still not available after retry`, { switchName });
            }
          }, 100);
        }
      });
    },
    [callWhenReady, switchMetadata, switchName],
  );

  const convertExperiment = useCallback(
    (onConverted?: () => void) =>
      callWhenReady(() => {
        logConvertExperiment(switchName);
        onConverted?.();
      }),
    [callWhenReady, switchName],
  );

  const isMobileOnly = "mobileOnly" in switchMetadata && switchMetadata.mobileOnly;
  const isMobile = useIsMobile();
  const excludeForMobileOnly = isMobileOnly && !isMobile;

  return isInAudience && !excludeForMobileOnly
    ? {
        data: featureSwitch,
        convertExperiment,
        startExperiment,
        isInExperiment: featureSwitch != null && featureSwitch !== "off" && featureSwitch !== "control",
      }
    : {
        data: "off" as FeatureSwitches[TSwitchName],
        convertExperiment: () => {},
        startExperiment: () => {},
        isInExperiment: false,
      };
};

export const useManualFeatureSwitch: typeof useManualFeatureSwitchValue = (...args) => {
  const value = useManualFeatureSwitchValue(...args);
  const cachedValueRef = useRef(value);
  const { ready: featureSwitchesLoaded, isWaitingForDependencies } = useEnsureFeatureSwitches();
  const isReady = featureSwitchesLoaded && !isWaitingForDependencies;

  if (isReady) cachedValueRef.current = value;
  return cachedValueRef.current;
};

export const useFeatureSwitchAndStartExperiment = <TSwitchName extends FeatureName>(
  rawSwitchName: TSwitchName,
  { isInAudience = true }: FeatureSwitchOptions = {},
) => {
  const { startExperiment, ...switchObject } = useManualFeatureSwitch(rawSwitchName, { isInAudience });

  useEffect(() => {
    if (switchObject.data && switchObject.data !== "off") {
      startExperiment();
    }
  }, [rawSwitchName, startExperiment, switchObject.data]);

  return switchObject;
};

export const useConvertExperimentOnLoad = (switchName: FeatureName, options?: FeatureSwitchOptions) => {
  const { convertExperiment } = useManualFeatureSwitch(switchName, { ...options });

  useOnFirstRender(() => {
    convertExperiment();
  });
};

export function buildFeatureSwitchesQueryParams({
  roles,
  sessionId,
  userId,
}: {
  roles?: string[];
  sessionId: string;
  userId?: string;
}): FeatureSwitchQueryParams {
  const loggedOutParams: LoggedOutFeatureSwitchQueryParams = {
    featureSetId: "1",
    sessionId,
    sessionSwitches: LOGGED_OUT_SWITCH_NAMES.join(","),
  };

  if (roles != null && roles.length > 0 && userId != null) {
    const role = getRole(roles);
    if (role === "tutor") {
      return {
        ...loggedOutParams,
        tutorId: userId,
        tutorCountryCode: "US",
        tutorSwitches: LOGGED_IN_SWITCH_NAMES.join(","),
      };
    }

    return {
      ...loggedOutParams,
      parentId: userId,
      parentCountryCode: "US",
      parentSwitches: LOGGED_IN_SWITCH_NAMES.join(","),
    };
  }

  return loggedOutParams;
}

const useBuildFeatureSwitchesQueryParamsHook = () => {
  const { isLoading, isFetching, sessionData } = useSessionQuery();
  if (isLoading || isFetching) return null;

  return buildFeatureSwitchesQueryParams({
    roles: sessionData?.roles,
    sessionId: logClient.getSessionId(),
    userId: sessionData?.user_details.uid,
  });
};

export function setAppFeatureSwitchesConfig() {
  setFeatureSwitchesConfig({
    featureSetIdLocalStorageKey: `marketplace/featureSwitches/featureSetId`,
    useBuildFeatureSwitchesQueryParamsHook,
    useFeatureSwitchOverridesHook: () => {
      const { featureSwitchOverrides } = DevToolsSettings.useFeatureSwitchOverrides();
      return featureSwitchOverrides;
    },
    featureSwitchesFetcherQueryParams: [
      "sessionId",
      "sessionSwitches",
      "parentId",
      "parentSwitches",
      "tutorId",
      "tutorSwitches",
      "parentCountryCode",
      "tutorCountryCode",
    ],
  });
}
